// Lightweight consent state — strict mode (nothing loads without explicit opt-in).

export type ConsentCategories = {
  analytics: boolean;
  ads: boolean;
};

export type ConsentState = ConsentCategories & {
  ts: number;
  version: number;
};

const KEY = "flyg.consent";
const VERSION = 1;
const EVENT = "flyg:consent-changed";

export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed || parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(c: ConsentCategories): ConsentState {
  const state: ConsentState = { ...c, ts: Date.now(), version: VERSION };
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: state }));
  }
  return state;
}

export function clearConsent() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
  }
}

export function onConsentChange(cb: (s: ConsentState | null) => void) {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => cb((e as CustomEvent).detail ?? null);
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}

export const CONSENT_EVENT = EVENT;

// UI command to (re)open the banner — used from footer "Cookie settings".
export const OPEN_CONSENT_EVENT = "flyg:consent-open";
export function openConsentSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT));
  }
}